import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { SatrackEvents } from "../dictionaries/satrack-events.dictionary";
import { FirebaseDatabase } from "./firebaseDatabase.service";
import { AmountsCargoEnum } from "../enums/amountsCargo.enum";
import { CargoLastEvent, LastEventIdle, LastEventReport, LastEventSeverity } from "../interfaces/cargoLastEvent";
import { MapInfoWindowChip, MapInfoWindowSection } from "../interfaces/mapInfoWindow";
import { DateManager } from "../managers/date.manager";

/**
 * Traduce el último evento GPS del vehículo (`cargo/<id>/lastEvent`) a los indicadores
 * que se pintan en el seguimiento del servicio.
 *
 * La antigüedad del reporte se calcula SIEMPRE en el cliente contra la hora actual: el
 * backend no la envía, y guardarla ya calculada la congelaría justo cuando el GPS deja
 * de reportar, que es el único momento en que importa.
 */
@Injectable()
export class CargoLastEventService {

  private static readonly SOURCE_LABELS = {
    satrack: 'Satrack',
    monitor: 'MonitorHub',
    smartelock: 'Smartelock'
  };

  constructor(private firebaseDatabase: FirebaseDatabase) { }

  /**
   * Escucha el último evento del servicio. Emite `null` mientras el nodo no exista
   * (servicio sin ningún reporte GPS todavía) y cierra el listener al desuscribirse.
   */
  public listen(cargoId: string): Observable<CargoLastEvent | null> {
    return new Observable<CargoLastEvent | null>(observer => {
      const reference = this.firebaseDatabase.getRefDatabase(`cargo/${cargoId}/lastEvent`);
      const callback = reference.on('value', (snapshot) => {
        observer.next(snapshot && snapshot.val() ? snapshot.val() : null);
      }, (error) => {
        console.error('Error en listener de lastEvent:', error);
        observer.next(null);
      });
      return () => reference.off('value', callback);
    });
  }

  /** Estado de reporte del vehículo. `now` se inyecta para poder testear el cálculo. */
  public getReport(event: CargoLastEvent, now: number = Date.now()): LastEventReport {
    const minutesAgo = this.getMinutesAgo(event, now);
    const reporting = this.isReporting(event, minutesAgo);
    return {
      reporting,
      label: this.getReportLabel(reporting),
      minutesAgo,
      elapsedLabel: minutesAgo === null ? '' : `hace ${this.formatMinutes(minutesAgo)}`,
      severity: this.getFreshnessSeverity(minutesAgo)
    };
  }

  /**
   * Tiempo detenido. Satrack y Smartelock miden cosas distintas y por eso se etiquetan
   * distinto: `samePlaceMinutes` es cuánto lleva el vehículo quieto EN ESTE PUNTO, mientras
   * que `todayIdlingSeconds` es el ralentí acumulado desde las 00:00. Unificarlos mostraría
   * "detenido hace 3 h" para un camión que lleva toda la mañana rodando.
   */
  public getIdle(event: CargoLastEvent): LastEventIdle {
    if (event && typeof event.samePlaceMinutes === 'number') {
      return this.buildIdle('Detenido', event.samePlaceMinutes);
    }
    if (event && typeof event.todayIdlingSeconds === 'number') {
      return this.buildIdle('Ralentí hoy', Math.floor(event.todayIdlingSeconds / 60));
    }
    return {
      available: false,
      label: 'Detenido',
      value: 'No disponible para esta fuente',
      severity: 'unknown'
    };
  }

  /** Nombre presentable de la integración que escribió el evento. */
  public getSourceLabel(event: CargoLastEvent): string {
    const source = this.getSourceKey(event);
    if (!source) return '';
    return CargoLastEventService.SOURCE_LABELS[source] || source;
  }

  /**
   * Novedad legible. Se prefiere la descripción del proveedor; el catálogo de códigos solo
   * se aplica a Satrack, porque en las otras fuentes `eventCode` es texto libre.
   */
  public getNovelty(event: CargoLastEvent): string {
    if (!event) return '';
    if (event.description) return event.description;
    if (!event.eventCode) return '';
    if (this.getSourceKey(event) === 'satrack') {
      return SatrackEvents[event.eventCode] || event.eventCode;
    }
    return event.eventCode;
  }

  /**
   * Filas de detalle del último reporte, para el globo del marcador.
   * Solo se incluye lo que la fuente realmente envió: una clave ausente se omite en vez de
   * pintarse en cero.
   */
  /**
   * Estado del vehículo como chips: lo que es estado se destaca, lo que es dato queda en
   * fila. Son los tres valores que un operador lee de un vistazo antes que cualquier otro.
   *
   * El cero es dato real: velocidad 0 e ignición apagada son justamente lo que hay que ver.
   * Se decide por existencia de la clave, nunca por el valor.
   *
   * Estado e ignición van etiquetados porque miden cosas distintas y a veces coinciden en el
   * texto: con el vehículo apagado los dos dicen 'Apagado' y se leían como un dato repetido,
   * mientras que un 'Detenido-Ralenti' con la ignición en 1 es un detenido con motor encendido.
   */
  public getStatusChips(event: CargoLastEvent): MapInfoWindowChip[] {
    if (!event) return [];
    const chips: MapInfoWindowChip[] = [];
    if (event.vehicleStatus) {
      chips.push({ text: `Estado: ${event.vehicleStatus}`, tone: 'accent' });
    }
    if (typeof event.ignition === 'number') {
      chips.push({ text: `Motor: ${event.ignition ? 'Encendido' : 'Apagado'}`, tone: 'neutral' });
    }
    if (typeof event.speed === 'number') {
      chips.push({ text: `${event.speed} km/h`, tone: 'neutral' });
    }
    return chips;
  }

  public getDetailRows(event: CargoLastEvent): Array<{ label: string, value: string }> {
    if (!event) return [];
    const rows: Array<{ label: string, value: string }> = [];
    const add = (label: string, value: string) => { if (value) rows.push({ label, value }); };

    // Estado, ignición y velocidad NO van aquí: se pintan como chips en getStatusChips().
    add('Fuente', this.getSourceLabel(event));
    add('Fecha del reporte', this.getReportDate(event));
    add('Novedad', this.getNovelty(event));
    if (typeof event.batteryLevel === 'number') {
      rows.push({ label: 'Batería GPS', value: `${event.batteryLevel} %` });
    }
    if (typeof event.odometer === 'number') rows.push({ label: 'Odómetro', value: `${event.odometer} km` });
    if (event.temperature) {
      rows.push({ label: 'Temperatura', value: event.temperature.split('|').join(' · ') });
    }
    add('Dirección', event.address);
    return rows;
  }

  /**
   * El detalle del último reporte listo para el globo del marcador. Vive acá y no en cada
   * mapa para que el rótulo de la sección no se escriba dos veces: lo usan el seguimiento
   * del servicio y el mapa global.
   */
  public getDetailSections(event: CargoLastEvent): MapInfoWindowSection[] {
    const rows = this.getDetailRows(event);
    if (!rows.length) return [];
    return [{ label: 'Último reporte GPS', rows }];
  }

  private getSourceKey(event: CargoLastEvent): string {
    return event && event.eventSource && event.eventSource.description
      ? event.eventSource.description
      : '';
  }

  /** Smartelock manda `gpsTime` en un formato que el proveedor no documenta: se muestra tal cual. */
  private getReportDate(event: CargoLastEvent): string {
    if (typeof event.recordDateEpoch === 'number') {
      // .slice(0, 16) recorta el offset, igual que el resto de los globos del mapa.
      const formatted = DateManager.formatDate(new Date(event.recordDateEpoch));
      return formatted ? formatted.slice(0, 16) : '';
    }
    return event.gpsTime || '';
  }

  private buildIdle(label: string, minutes: number): LastEventIdle {
    return {
      available: true,
      label,
      value: this.formatMinutes(minutes),
      severity: this.getIdleSeverity(minutes)
    };
  }

  /** `null` cuando la fuente no envía una fecha utilizable (Smartelock manda `gpsTime` sin parsear). */
  private getMinutesAgo(event: CargoLastEvent, now: number): number | null {
    if (!event || typeof event.recordDateEpoch !== 'number') return null;
    return Math.max(0, Math.floor((now - event.recordDateEpoch) / 60000));
  }

  /**
   * Cada fuente declara el estado a su manera; solo si ninguna lo declara se deriva de la
   * antigüedad del reporte. `null` significa "no se puede afirmar", nunca "sin reportar".
   */
  private isReporting(event: CargoLastEvent, minutesAgo: number | null): boolean | null {
    if (!event) return null;
    if (typeof event.locationStatus === 'string') {
      return event.locationStatus.trim().toLowerCase() === 'reportando';
    }
    if (typeof event.online === 'boolean') return event.online;
    if (minutesAgo === null) return null;
    return minutesAgo <= AmountsCargoEnum.HIGH_ALERT_MINS;
  }

  private getReportLabel(reporting: boolean | null): string {
    if (reporting === null) return 'Sin información';
    return reporting ? 'Reportando' : 'Sin reportar';
  }

  /** Mismos cortes que el semáforo de la tarjeta en servicios en ruta, para no contradecirlo. */
  private getFreshnessSeverity(minutesAgo: number | null): LastEventSeverity {
    if (minutesAgo === null) return 'unknown';
    if (minutesAgo <= AmountsCargoEnum.HIGH_ALERT_MINS) return 'fresh';
    if (minutesAgo <= AmountsCargoEnum.MEDIUM_ALERT_MINS) return 'ok';
    if (minutesAgo <= AmountsCargoEnum.LOW_ALERT_MINS) return 'warning';
    if (minutesAgo <= AmountsCargoEnum.EMPTY_ALERT_MINS) return 'danger';
    return 'stale';
  }

  private getIdleSeverity(minutes: number): LastEventSeverity {
    if (minutes < AmountsCargoEnum.ALARM_YELLOW_MINS_STILL) return 'ok';
    if (minutes < AmountsCargoEnum.ALARM_ORANGE_MINS_STILL) return 'warning';
    return 'danger';
  }

  private formatMinutes(value: number): string {
    // El backend declara samePlaceMinutes como Double (Satrack manda 143.0).
    const totalMinutes = Math.floor(value);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (!hours) return `${minutes} min`;
    return minutes ? `${hours} h ${minutes} min` : `${hours} h`;
  }
}
